import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Package, Plus, ArrowUUpLeft } from "@phosphor-icons/react";
import { PartPicker } from "./MovementDialog";
import { money, qty as fq, fmtDT } from "./stockUtils";

/** Parts used on a job — issued from a van/store, recorded as job cost only. Operatives default to their own van. */
export function JobPartsPanel({ jobId, canIssue = true, isAdmin = false }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [parts, setParts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [f, setF] = useState({ location_id: "", part_id: "", qty: "1", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => API.get(`/jobs/${jobId}/parts`).then((r) => setData(r.data)).catch(() => setData({ movements: [], parts_cost: 0, parts_qty: 0, my_van: null })), [jobId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!adding) return;
    Promise.all([API.get("/stock/parts"), API.get("/stock/locations")]).then(([p, l]) => {
      const def = data?.my_van?.id || (l.data.locations.find((x) => x.type === "store") || l.data.locations[0])?.id || "";
      setF((prev) => ({ ...prev, location_id: prev.location_id || def }));
      setParts(p.data.parts); setLocations(l.data.locations);
    }).catch(() => {});
  }, [adding, data?.my_van?.id]);

  const issue = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post("/stock/movements", { type: "issue", job_id: jobId, part_id: f.part_id, location_id: f.location_id, qty: Number(f.qty), note: f.note });
      toast.success("Parts recorded against the job");
      setF((p) => ({ ...p, part_id: "", qty: "1", note: "" }));
      setAdding(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't issue the parts"); } finally { setSaving(false); }
  };
  const giveBack = async (m) => {
    if (!window.confirm(`Return ${fq(m.qty)} × ${m.part_name} to ${m.location_name}?`)) return;
    try { await API.post("/stock/movements", { type: "return", job_id: jobId, part_id: m.part_id, location_id: m.location_id, qty: m.qty, note: `Returned from job` }); toast.success("Parts returned to stock"); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't return"); }
  };

  if (!data) return null;
  const rows = data.movements;
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="job-parts-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-lg font-semibold flex items-center gap-2"><Package size={20} /> Parts used <span className="text-xs font-normal text-muted-foreground">(job cost only — not invoiced automatically)</span></h3>
        <div className="flex items-center gap-3">
          <span className="text-sm">{fq(data.parts_qty)} item{data.parts_qty === 1 ? "" : "s"} · <b className="font-mono" data-testid="job-parts-cost">{money(data.parts_cost)}</b></span>
          {canIssue && !adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="job-parts-add"><Plus size={14} className="mr-1" /> Add parts</Button>}
        </div>
      </div>
      {adding && (
        <form onSubmit={issue} className="rounded-md border border-primary/40 bg-primary/5 p-3 mb-3 space-y-3" data-testid="job-parts-form">
          <div className="grid sm:grid-cols-[1fr_120px] gap-3 items-end">
            <div><Label>From</Label>
              {locations.length === 0 ? <div className="h-9 rounded-md border border-input bg-muted/30 animate-pulse" data-testid="job-parts-location-loading" /> : (
              <Select value={f.location_id} onValueChange={(v) => setF((p) => ({ ...p, location_id: v }))}><SelectTrigger data-testid="job-parts-location"><SelectValue placeholder="Van or store" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.type === "van" ? ` (van${l.id === data.my_van?.id ? " · mine" : l.operative_name ? ` · ${l.operative_name}` : ""})` : ""}</SelectItem>)}</SelectContent></Select>)}</div>
            <div><Label>Quantity</Label><Input type="number" min="0" step="any" required value={f.qty} onChange={(e) => setF((p) => ({ ...p, qty: e.target.value }))} data-testid="job-parts-qty" /></div>
          </div>
          <div><Label>Part</Label><PartPicker parts={parts} value={f.part_id} onChange={(v) => setF((p) => ({ ...p, part_id: v }))} locationId={f.location_id} testid="job-parts-part" /></div>
          {parts.length === 0 && <p className="text-xs text-muted-foreground">No parts in the catalogue yet.{isAdmin && <> Set them up under <Link to="/stock" className="text-primary hover:underline">Stock &amp; Parts</Link>.</>}</p>}
          <div className="flex items-end gap-2"><div className="flex-1"><Label>Note</Label><Input value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} placeholder="Optional — e.g. replaced belt on AHU-2" data-testid="job-parts-note" /></div>
            <Button type="button" variant="outline" onClick={() => setAdding(false)}>Cancel</Button><Button type="submit" disabled={saving || !f.part_id || !f.location_id} data-testid="job-parts-save">{saving ? "Saving…" : "Record"}</Button></div>
        </form>
      )}
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">No parts recorded on this job yet.{canIssue ? " Add the spares fitted so stock stays right and the cost lands on the job." : ""}</p> : (
        <div className="border border-border rounded-md divide-y divide-border text-sm" data-testid="job-parts-list">
          {rows.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`job-part-${m.id}`}>
              <div className="min-w-0"><p className="font-medium">{m.type === "return" ? <span className="text-muted-foreground">Returned </span> : ""}{fq(m.qty)} × {m.part_name}{m.part_number && <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{m.part_number}</span>}</p>
                <p className="text-[11px] text-muted-foreground truncate">{m.location_name} · {m.by} · {fmtDT(m.at)}{m.note ? ` · ${m.note}` : ""}</p></div>
              <div className="flex items-center gap-2 shrink-0"><span className={`font-mono text-xs ${m.type === "return" ? "text-muted-foreground" : ""}`}>{m.type === "return" ? "−" : ""}{money(m.total_cost)}</span>
                {canIssue && m.type === "issue" && <Button variant="ghost" size="icon" className="h-7 w-7" title="Return to stock" onClick={() => giveBack(m)} data-testid={`job-part-return-${m.id}`}><ArrowUUpLeft size={14} /></Button>}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
